import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, LogOut, Activity, FileText, Shield, BarChart3, Eye, Trash2, Ban, RefreshCw, Clock, Globe, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [traffic, setTraffic] = useState({ logs: [], total: 0 });
  const [quotes, setQuotes] = useState({ quotes: [], total: 0 });
  const [blockedIps, setBlockedIps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trafficPage, setTrafficPage] = useState(1);

  const headers = { Authorization: `Bearer ${token}` };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', res.data.token);
      setToken(res.data.token);
      toast.success('Logged in');
    } catch {
      toast.error('Invalid password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
  };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [a, t, q, b] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/traffic?limit=100&page=${trafficPage}`, { headers }),
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/blocked-ips`, { headers }),
      ]);
      setAnalytics(a.data);
      setTraffic(t.data);
      setQuotes(q.data);
      setBlockedIps(b.data.ips);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired');
      }
    } finally {
      setLoading(false);
    }
  }, [token, trafficPage]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteQuote = async (id) => {
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { headers });
      toast.success('Quote deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const blockIp = async (ip) => {
    try {
      await axios.post(`${API}/admin/block-ip`, { ip }, { headers });
      toast.success(`Blocked ${ip}`);
      fetchAll();
    } catch { toast.error('Failed to block'); }
  };

  const unblockIp = async (ip) => {
    try {
      await axios.delete(`${API}/admin/block-ip/${ip}`, { headers });
      toast.success(`Unblocked ${ip}`);
      fetchAll();
    } catch { toast.error('Failed to unblock'); }
  };

  // --- Login Screen ---
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center p-4">
        <Card className="bg-white/5 border border-white/10 w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Shield className="w-12 h-12 text-[#00D9FF] mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-gray-400 text-sm mt-2">Chill Point Traffic Monitor</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                data-testid="admin-password-input"
              />
              <Button type="submit" className="w-full bg-gradient-to-r from-[#0066FF] to-[#00D9FF] text-white font-bold" data-testid="admin-login-btn">
                Login
              </Button>
            </form>
            <a href="/" className="flex items-center justify-center space-x-2 text-gray-500 hover:text-[#00D9FF] mt-6 text-sm">
              <ArrowLeft className="w-4 h-4" /><span>Back to site</span>
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'traffic', label: 'Traffic Logs', icon: <Activity className="w-4 h-4" /> },
    { id: 'quotes', label: 'Quote Requests', icon: <FileText className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-[#0A0E27]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#0D1233] border-b border-white/5 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-[#00D9FF]" />
            <h1 className="text-white font-bold text-lg">Chill Point Admin</h1>
          </div>
          <div className="flex items-center space-x-3">
            <Button size="sm" variant="ghost" onClick={fetchAll} className="text-gray-400 hover:text-white" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleLogout} className="text-gray-400 hover:text-red-400" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[#0D1233]/50 border-b border-white/5 px-4">
        <div className="max-w-7xl mx-auto flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-[#00D9FF] text-[#00D9FF]' : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}<span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {activeTab === 'overview' && <OverviewTab analytics={analytics} />}
        {activeTab === 'traffic' && <TrafficTab traffic={traffic} page={trafficPage} setPage={setTrafficPage} onBlock={blockIp} />}
        {activeTab === 'quotes' && <QuotesTab quotes={quotes} onDelete={deleteQuote} onBlock={blockIp} />}
        {activeTab === 'security' && <SecurityTab blockedIps={blockedIps} onUnblock={unblockIp} onBlock={blockIp} />}
      </div>
    </div>
  );
};

// --- Overview Tab ---
const OverviewTab = ({ analytics }) => {
  if (!analytics) return <div className="text-gray-400">Loading...</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Requests', value: analytics.total_traffic, icon: <Globe className="w-5 h-5" />, color: 'from-[#0066FF] to-[#00D9FF]' },
          { label: 'Quote Requests', value: analytics.total_quotes, icon: <FileText className="w-5 h-5" />, color: 'from-green-400 to-emerald-500' },
          { label: 'Unique IPs', value: analytics.top_ips?.length || 0, icon: <Monitor className="w-5 h-5" />, color: 'from-purple-500 to-pink-500' },
          { label: 'Endpoints Hit', value: analytics.top_paths?.length || 0, icon: <Activity className="w-5 h-5" />, color: 'from-yellow-400 to-orange-500' },
        ].map((stat, i) => (
          <Card key={i} className="bg-white/5 border border-white/10">
            <CardContent className="p-5">
              <div className={`w-10 h-10 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center text-white mb-3`}>{stat.icon}</div>
              <div className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top IPs */}
        <Card className="bg-white/5 border border-white/10">
          <CardContent className="p-6">
            <h3 className="text-white font-bold mb-4 flex items-center space-x-2"><Globe className="w-4 h-4 text-[#00D9FF]" /><span>Top IPs</span></h3>
            <div className="space-y-2">
              {analytics.top_ips?.slice(0, 10).map((ip, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-300 font-mono text-xs">{ip.ip}</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-24 bg-white/5 rounded-full h-2">
                      <div className="bg-[#00D9FF] h-2 rounded-full" style={{ width: `${Math.min(100, (ip.count / (analytics.top_ips[0]?.count || 1)) * 100)}%` }} />
                    </div>
                    <span className="text-gray-400 w-12 text-right">{ip.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Paths */}
        <Card className="bg-white/5 border border-white/10">
          <CardContent className="p-6">
            <h3 className="text-white font-bold mb-4 flex items-center space-x-2"><Activity className="w-4 h-4 text-[#00D9FF]" /><span>Top Endpoints</span></h3>
            <div className="space-y-2">
              {analytics.top_paths?.slice(0, 10).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-300 font-mono text-xs truncate max-w-[200px]">{p.path}</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-24 bg-white/5 rounded-full h-2">
                      <div className="bg-[#0066FF] h-2 rounded-full" style={{ width: `${Math.min(100, (p.count / (analytics.top_paths[0]?.count || 1)) * 100)}%` }} />
                    </div>
                    <span className="text-gray-400 w-12 text-right">{p.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Status Codes */}
        <Card className="bg-white/5 border border-white/10">
          <CardContent className="p-6">
            <h3 className="text-white font-bold mb-4 flex items-center space-x-2"><Eye className="w-4 h-4 text-[#00D9FF]" /><span>Status Codes</span></h3>
            <div className="space-y-3">
              {analytics.status_codes?.map((s, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className={`font-mono text-sm font-bold ${s.code >= 500 ? 'text-red-400' : s.code >= 400 ? 'text-yellow-400' : s.code >= 300 ? 'text-blue-400' : 'text-green-400'}`}>
                    {s.code}
                  </span>
                  <span className="text-gray-400 text-sm">{s.count} requests</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hourly Traffic */}
        <Card className="bg-white/5 border border-white/10">
          <CardContent className="p-6">
            <h3 className="text-white font-bold mb-4 flex items-center space-x-2"><Clock className="w-4 h-4 text-[#00D9FF]" /><span>Recent Hourly Traffic</span></h3>
            <div className="space-y-2">
              {analytics.hourly_traffic?.slice(0, 12).map((h, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 text-xs">{h.hour}</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-24 bg-white/5 rounded-full h-2">
                      <div className="bg-gradient-to-r from-[#0066FF] to-[#00D9FF] h-2 rounded-full" style={{ width: `${Math.min(100, (h.count / (analytics.hourly_traffic[0]?.count || 1)) * 100)}%` }} />
                    </div>
                    <span className="text-gray-400 w-12 text-right">{h.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// --- Traffic Logs Tab ---
const TrafficTab = ({ traffic, page, setPage, onBlock }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-white font-bold">Traffic Logs ({traffic.total} total)</h3>
      <div className="flex items-center space-x-2">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-gray-400">Prev</Button>
        <span className="text-gray-400 text-sm">Page {page}</span>
        <Button size="sm" variant="ghost" onClick={() => setPage(p => p + 1)} className="text-gray-400">Next</Button>
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 border-b border-white/10 text-left">
            <th className="p-3">Time</th>
            <th className="p-3">Method</th>
            <th className="p-3">Path</th>
            <th className="p-3">Status</th>
            <th className="p-3">IP</th>
            <th className="p-3">Duration</th>
            <th className="p-3">Action</th>
          </tr>
        </thead>
        <tbody>
          {traffic.logs?.map((log, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
              <td className="p-3 text-gray-400 text-xs whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
              <td className="p-3"><span className={`font-mono text-xs font-bold ${log.method === 'POST' ? 'text-yellow-400' : log.method === 'DELETE' ? 'text-red-400' : 'text-green-400'}`}>{log.method}</span></td>
              <td className="p-3 text-gray-300 font-mono text-xs max-w-[200px] truncate">{log.path}</td>
              <td className="p-3"><span className={`font-mono text-xs font-bold ${log.status_code >= 400 ? 'text-red-400' : 'text-green-400'}`}>{log.status_code}</span></td>
              <td className="p-3 text-gray-300 font-mono text-xs">{log.ip_address}</td>
              <td className="p-3 text-gray-400 text-xs">{log.duration_ms}ms</td>
              <td className="p-3">
                <button onClick={() => onBlock(log.ip_address)} className="text-red-400 hover:text-red-300 text-xs" title="Block IP">
                  <Ban className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// --- Quotes Tab ---
const QuotesTab = ({ quotes, onDelete, onBlock }) => (
  <div className="space-y-4">
    <h3 className="text-white font-bold">Quote Requests ({quotes.total})</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 border-b border-white/10 text-left">
            <th className="p-3">Date</th>
            <th className="p-3">Name</th>
            <th className="p-3">Email</th>
            <th className="p-3">Phone</th>
            <th className="p-3">Service</th>
            <th className="p-3">IP</th>
            <th className="p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {quotes.quotes?.map((q, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
              <td className="p-3 text-gray-400 text-xs whitespace-nowrap">{new Date(q.created_at).toLocaleString()}</td>
              <td className="p-3 text-white font-medium text-xs">{q.name}</td>
              <td className="p-3 text-gray-300 text-xs">{q.email}</td>
              <td className="p-3 text-gray-300 text-xs">{q.phone}</td>
              <td className="p-3 text-[#00D9FF] text-xs">{q.service}</td>
              <td className="p-3 text-gray-400 font-mono text-xs">{q.ip_address || 'N/A'}</td>
              <td className="p-3">
                <div className="flex items-center space-x-2">
                  <button onClick={() => onDelete(q.quote_id)} className="text-red-400 hover:text-red-300" title="Delete">
                    <Trash2 className="w-3 h-3" />
                  </button>
                  {q.ip_address && (
                    <button onClick={() => onBlock(q.ip_address)} className="text-yellow-400 hover:text-yellow-300" title="Block IP">
                      <Ban className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// --- Security Tab ---
const SecurityTab = ({ blockedIps, onUnblock, onBlock }) => {
  const [newIp, setNewIp] = useState('');
  return (
    <div className="space-y-6">
      <Card className="bg-white/5 border border-white/10">
        <CardContent className="p-6">
          <h3 className="text-white font-bold mb-4 flex items-center space-x-2"><Ban className="w-4 h-4 text-red-400" /><span>Block IP Manually</span></h3>
          <div className="flex space-x-3">
            <Input
              placeholder="Enter IP address to block"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 max-w-sm"
              data-testid="block-ip-input"
            />
            <Button onClick={() => { if (newIp) { onBlock(newIp); setNewIp(''); } }} className="bg-red-500 hover:bg-red-600 text-white" data-testid="block-ip-btn">
              Block
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border border-white/10">
        <CardContent className="p-6">
          <h3 className="text-white font-bold mb-4">Blocked IPs ({blockedIps.length})</h3>
          {blockedIps.length === 0 ? (
            <p className="text-gray-400 text-sm">No blocked IPs</p>
          ) : (
            <div className="space-y-2">
              {blockedIps.map((item, i) => (
                <div key={i} className="flex items-center justify-between bg-white/5 p-3 rounded-lg">
                  <div>
                    <span className="text-white font-mono text-sm">{item.ip}</span>
                    <span className="text-gray-500 text-xs ml-3">Blocked: {new Date(item.blocked_at).toLocaleString()}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => onUnblock(item.ip)} className="text-green-400 hover:text-green-300 text-xs">
                    Unblock
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
